// Options de `SearchableSelect` pour les entités qu'on choisit partout.
//
// Un camion se cherche par sa plaque, mais aussi par son chauffeur, sa
// citerne ou son transporteur : quelqu'un qui cherche « le camion de Koné »
// doit le trouver sans connaître la plaque. Une seule définition ici pour que
// chaque écran cherche de la même façon.

import { libelle, nomPays, estIdentifiant, referenceLisible } from './libelles';
import { formatDate } from './formats';

// « Camion loué » en sous-libellé: on ne confond plus un camion pris en
// location chez un transporteur avec un camion de la société.
export const reperePropriete = (truck) =>
  truck && truck.ownership_type === 'LOUE' ? libelle('truckOwnership', 'LOUE') : undefined;

export const truckOption = (truck) => ({
  value: truck.id,
  label: truck.matricule || 'Camion sans immatriculation',
  description:
    [truck.driver_full_name, reperePropriete(truck)].filter(Boolean).join(' · ') || undefined,
  keywords: [
    truck.tank_number,
    truck.tractor_vin,
    truck.tank_vin,
    truck.company_name,
    truck.matricule_normalized,
  ],
});

export const driverOption = (driver) => ({
  value: driver.id,
  label: driver.full_name || 'Chauffeur sans nom',
  description: driver.phone || undefined,
  keywords: [driver.license_number],
});

export const organizationOption = (organization) => ({
  value: organization.id,
  label: organization.name,
  keywords: [organization.city, organization.email],
});

const texteLu = (valeur) => {
  if (valeur === null || valeur === undefined) return null;
  const texte = String(valeur).trim();
  if (!texte || texte === 'null' || texte === 'undefined' || estIdentifiant(texte)) return null;
  return texte;
};

const indexerParId = (lignes) =>
  new Map((Array.isArray(lignes) ? lignes : []).filter((ligne) => ligne?.id).map((ligne) => [ligne.id, ligne]));

/**
 * Un trajet se reconnaît à sa plaque, son itinéraire et sa date de départ:
 * « AA-1234-MD · Bamako → Dakar · 12/10/2026 ». Jamais un code pays seul, ni un
 * identifiant. L'étape en description; chauffeur, destination et convoi se
 * cherchent aussi.
 */
export const tripOption = (trip, { trucks = [], drivers = [], operations = [] } = {}) => {
  const camion = indexerParId(trucks).get(trip.truck_id);
  const chauffeur = indexerParId(drivers).get(trip.driver_id);
  const convoi = indexerParId(operations).get(trip.operation_id);

  const plaque = texteLu(trip.tractor_plate) || texteLu(camion?.matricule) || texteLu(trip.truck_matricule) || texteLu(trip.truck_plate);
  const depart = texteLu(trip.departure_city) || (texteLu(trip.departure_country) && nomPays(trip.departure_country, null));
  const arrivee = texteLu(trip.destination_city) || (texteLu(trip.destination_country) && nomPays(trip.destination_country, null));
  const itineraire = depart && arrivee ? `${depart} → ${arrivee}` : arrivee ? `Vers ${arrivee}` : depart ? `Depuis ${depart}` : null;
  const date = formatDate(trip.departure_date || trip.created_at, null);
  const nomChauffeur = texteLu(chauffeur?.full_name) || texteLu(trip.driver_full_name) || texteLu(trip.driver_name);
  const numeroConvoi = texteLu(convoi?.code) || texteLu(trip.operation_code) || texteLu(trip.convoy_code);
  const etape = trip.status ? libelle('etape_trajet', trip.status) : undefined;

  return {
    value: trip.id,
    label: [plaque, itineraire, date].filter(Boolean).join(' · ') || 'Trajet sans détail',
    description: [etape, nomChauffeur].filter(Boolean).join(' · ') || undefined,
    keywords: [
      plaque,
      trip.tank_plate,
      nomChauffeur,
      depart,
      arrivee,
      numeroConvoi && referenceLisible(numeroConvoi, null),
      convoi?.route,
    ].filter(Boolean),
  };
};

/** Libellé court d'un trajet pour une carte: « AA-1234-MD · Bamako → Dakar ». */
export const tripResume = (trip, contexte) => {
  return tripOption(trip, contexte).label.split(' · ').slice(0, 2).join(' · ');
};
